import { ScrollArea } from '@/components/ui/scroll-area';
import { Badge } from '@/components/ui/badge';

// Category images - using gradients and emojis for visual appeal
const CATEGORY_IMAGES = {
  'all': { emoji: '🍽️', gradient: 'from-orange-400 to-red-500', label: 'All' },
  'popular': { emoji: '⭐', gradient: 'from-yellow-400 to-orange-500', label: 'Popular' },
  'appetizers': { emoji: '🥗', gradient: 'from-green-400 to-emerald-500', label: 'Appetizers' },
  'appetizer': { emoji: '🥗', gradient: 'from-green-400 to-emerald-500', label: 'Appetizers' },
  'soup': { emoji: '🍜', gradient: 'from-amber-400 to-orange-500', label: 'Soup' },
  'starter': { emoji: '🍢', gradient: 'from-rose-400 to-pink-500', label: 'Starter' },
  'starters': { emoji: '🍢', gradient: 'from-rose-400 to-pink-500', label: 'Starters' },
  'salad': { emoji: '🥬', gradient: 'from-lime-400 to-green-500', label: 'Salad' },
  'main': { emoji: '🍛', gradient: 'from-orange-400 to-red-500', label: 'Main' },
  'main course': { emoji: '🍛', gradient: 'from-orange-400 to-red-500', label: 'Main Course' },
  'punjabi': { emoji: '🍲', gradient: 'from-amber-500 to-orange-600', label: 'Punjabi' },
  'south indian': { emoji: '🥘', gradient: 'from-yellow-400 to-amber-500', label: 'South Indian' },
  'seafood': { emoji: '🦐', gradient: 'from-blue-400 to-cyan-500', label: 'Seafood' },
  'gujarati': { emoji: '🫓', gradient: 'from-orange-300 to-amber-500', label: 'Gujarati' },
  'dessert': { emoji: '🍰', gradient: 'from-pink-400 to-rose-500', label: 'Dessert' },
  'desserts': { emoji: '🍰', gradient: 'from-pink-400 to-rose-500', label: 'Desserts' },
  'beverages': { emoji: '🥤', gradient: 'from-cyan-400 to-blue-500', label: 'Beverages' },
  'drinks': { emoji: '🍹', gradient: 'from-purple-400 to-indigo-500', label: 'Drinks' },
  'chinese': { emoji: '🥡', gradient: 'from-red-400 to-rose-500', label: 'Chinese' },
  'italian': { emoji: '🍝', gradient: 'from-green-500 to-emerald-600', label: 'Italian' },
  'continental': { emoji: '🍽️', gradient: 'from-slate-400 to-gray-500', label: 'Continental' },
};

const getCategoryInfo = (category) => {
  const key = category?.toLowerCase() || 'all';
  return CATEGORY_IMAGES[key] || { 
    emoji: '🍴', 
    gradient: 'from-gray-400 to-gray-500', 
    label: category || 'Other' 
  };
};

const MenuGrid = ({ 
  menuItems, 
  cart = [],
  selectedCategory, 
  categories, 
  onSelectCategory, 
  searchQuery, 
  loading, 
  onAddToCart 
}) => {
  // Filter menu items
  const filteredItems = menuItems.filter(item => {
    const matchesSearch = item.name.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCategory = selectedCategory === 'all' || item.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  // Count items per category
  const getCategoryItemCount = (category) => {
    if (category === 'all') return menuItems.length;
    return menuItems.filter(item => item.category === category).length;
  };

  // Get quantity in cart for an item
  const getItemCartQuantity = (itemId) => {
    const cartItem = cart.find(c => c.item_id === itemId);
    return cartItem?.quantity || 0;
  };

  return (
    <div className="flex-1 flex overflow-hidden">
      {/* Category Sidebar with Images */}
      <div className="w-28 bg-white border-r overflow-hidden flex flex-col">
        <ScrollArea className="flex-1">
          <div className="py-2">
            {categories.map(category => {
              const info = getCategoryInfo(category);
              const itemCount = getCategoryItemCount(category);
              const isSelected = selectedCategory === category;
              
              return (
                <div
                  key={category}
                  className={`relative cursor-pointer transition-all mx-2 mb-2 rounded-lg overflow-hidden ${
                    isSelected ? 'ring-2 ring-orange-500 ring-offset-1' : 'hover:opacity-90'
                  }`}
                  onClick={() => onSelectCategory(category)}
                  data-testid={`category-${category}`}
                >
                  {/* Category Image/Gradient Background */}
                  <div className={`h-16 bg-gradient-to-br ${info.gradient} flex items-center justify-center`}>
                    <span className="text-3xl">{info.emoji}</span>
                  </div>
                  
                  {/* Item Count Badge */}
                  {itemCount > 0 && (
                    <Badge className="absolute top-1 right-1 h-5 min-w-[20px] p-0 flex items-center justify-center bg-blue-500 text-white text-xs font-bold">
                      {itemCount}
                    </Badge>
                  )}
                  
                  {/* Category Label */}
                  <div className={`py-1.5 text-center ${isSelected ? 'bg-orange-50' : 'bg-white'}`}>
                    <span className={`text-xs font-semibold ${isSelected ? 'text-orange-600' : 'text-gray-700'}`}>
                      {info.label}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        </ScrollArea>
      </div>

      {/* Menu Items Grid - 3 columns like reference */}
      <div className="flex-1 overflow-y-auto p-4 bg-gray-50">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="h-10 w-10 rounded-full border-4 border-orange-500 border-t-transparent animate-spin" />
          </div>
        ) : filteredItems.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 text-gray-400">
            <span className="text-4xl mb-2">🍽️</span>
            <p>No items found</p>
          </div>
        ) : (
          <div className="grid grid-cols-3 gap-3">
            {filteredItems.map(item => {
              const cartQty = getItemCartQuantity(item.id);
              const info = getCategoryInfo(item.category);
              
              return (
                <div 
                  key={item.id} 
                  data-testid={`menu-item-${item.id}`}
                  className="relative cursor-pointer bg-white border border-gray-200 rounded-lg hover:border-orange-400 hover:shadow-md transition-all duration-200 overflow-hidden"
                  onClick={() => onAddToCart(item)}
                >
                  {/* Item Card Content */}
                  <div className="p-3 text-center">
                    <p className="font-medium text-sm text-gray-800 leading-tight min-h-[40px] flex items-center justify-center">
                      {item.name}
                    </p>
                  </div>
                  
                  {/* Quantity Badge when in cart */}
                  {cartQty > 0 && (
                    <Badge className="absolute top-2 right-2 h-6 w-6 p-0 flex items-center justify-center bg-blue-500 text-white text-sm font-bold rounded-full">
                      {cartQty}
                    </Badge>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default MenuGrid;
